import React, { useRef, useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import CMSFormSelect from "../../formFields/CMSFormSelect";
import CMSFormTextArea from "../../formFields/CMSFormTextArea";
import GeneralButton from "../../common/GeneralButton";
import CustomAlert from "../../common/CustomAlert";
import CMSFormImage from "../../formFields/CMSFormImage";
import ContainerLoading from "../../common/loading/ContainerLoading";
import { useToast } from "../../contexts/ToastContext";
import { productReturnSchema } from "../../utils/validations";
import { useNavigate, useParams } from "react-router-dom";
import CMSFormInput from "../../formFields/CMSFormInput";
import axios from "../../../config/axios";

const OrderReturn = ({ order, onSubmit }) => {
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [error, setError] = useState("");
  const { id, productId } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const itemRef = useRef(null); // for dynamic product refs
  const handleSubmit = async (values, { resetForm }) => {
    setStatus("loading");

    try {
      const formData = new FormData();
      for (let name in values) {
        if (Array.isArray(values[name])) {
          formData.append(name, JSON.stringify(values[name]));
        } else {
          formData.append(name, values[name]);
        }
      }
      await axios.post("/dashboard/product-return", formData);

      setStatus("success");
      // navigate(-1);
      resetForm();
      setTimeout(() => {
       navigate(-1); 
      }, 100);
    } catch (err) {
      console.error(err);
      setStatus("idle");
      if (err.status === 409) {
        setError("You have already requested for this product return");
        setTimeout(() => {
          setError("");
        }, 5000);
      } else {
        setError("Submission failed. Please try again.");
        setTimeout(() => {
          setError("");
        }, 5000);
      }
    }
  };
  const refs = {
    reason: useRef(null),
    message: useRef(null),
    file: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  return (
    <div className="mt-6 flex flex-col gap-6 bg-[#DEE2DD]/40 rounded-[15px] p-6 lg:px-10 w-full relative">
      <div className="p-4">
        <h2 className="text-28pt mb-4">Return Product</h2>
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
            <ContainerLoading />
          </div>
        )}
        {/* Order Summary */}
        <div className="p-4 rounded-lg mb-6 bg-white shadow-sm flex flex-col sm:flex-row sm:justify-between items-start sm:items-center">
          {/* Order ID */}
          <p className="font-medium text-20pt text-gray-900 mb-2 sm:mb-0">
            Order ID: {order?.orderId}
          </p>

          {/* Status Badge */}
          <span
            className={`px-3 py-1 rounded-full text-sm  ${
              order?.orderStatus === "0"
                ? "bg-yellow text-primary"
                : order?.orderStatus === "1"
                ? "bg-green text-white"
                : order?.orderStatus === "2"
                ? "bg-red-500 text-white"
                : "bg-gray text-gray-800"
            }`}
          >
            {order?.orderStatus === "0"
              ? "Pending"
              : order?.orderStatus === "1"
              ? "Delivered"
              : order?.orderStatus === "2"
              ? "Cancelled"
              : ""}
          </span>
        </div>

        <Formik
          initialValues={{
            orderId: order._id,
            productId: productId,
            reason: "",
            message: "",
            file: null,
          }}
          validationSchema={productReturnSchema}
          onSubmit={(values, { resetForm }) =>
            handleSubmit(values, { resetForm })
          }
        >
          {(form) => (
            <form onSubmit={form.handleSubmit} className="space-y-4  relative">
              {/* Products */}
              <div className="relative ">
                {order.orderedItems
                  .filter((item) => item.productId === productId)
                  .map((product, index) => (
                    <div
                      key={index}
                      ref={itemRef}
                      className="rounded-lg p-6 flex flex-col gap-2 bg-white "
                    >
                      {/* Product Row */}
                      <div className="flex items-center gap-4 ">
                        <img
                          src={
                            process.env.REACT_APP_BACKEND_URL +
                            "public/" +
                            product?.product?.image
                          }
                          onError={(e) => {
                            e.currentTarget.src = require("../../../assets/images/link.png");
                          }}
                          className="w-20 h-20 object-cover rounded bg-gray "
                        />
                        <div className="flex-1 flex flex-col gap-1">
                          <p className="font-medium text-18pt">
                            {product?.product?.title}
                          </p>

                          <p className="text-16pt text-quaternary font-mono">
                            Qty: {product.quantity}
                          </p>
                        </div>
                        <div className="text-18pt text-gray-900 mt-2 sm:mt-0 text-right text-nowrap">
                          <p className="font-mono">{product.price} AED each</p>
                          <p className="font-sans text-20pt text-orange mt-1 ">
                            {product.price * product.quantity} AED
                          </p>
                        </div>
                      </div>

                      {/* Selected Item Details */}

                      <div className="space-y-10 mt-4 pb-4">
                        {/* Reason */}
                        <div className="relative">
                          <CMSFormInput
                            name={`reason`}
                            label="Reason for Return"
                            className="1"
                            form={form}
                            ref={refs.reason}
                            required
                          />
                          {form.errors.items &&
                            form.errors.items[index]?.reason &&
                            form.touched.items &&
                            form.touched.items[index]?.reason && (
                              <p className="errorMessage">
                                {form.errors.items[index].reason}
                              </p>
                            )}
                        </div>
                        {/* Comments */}

                        {/* Upload Photo */}
                        <CMSFormImage
                          name={`file`}
                          label="Upload Product Photo"
                          smallDescription="( Proof for return)"
                          placeholder=""
                          ref={refs.file}
                          required
                          form={form}
                        />
                        <CMSFormTextArea
                          name={`message`}
                          label="Additional Comments"
                          ref={refs.message}
                          placeholder=""
                          required
                          maxLength={300}
                          form={form}
                        />
                      </div>
                       {error && (
                    <div
                      className={`relative transition-all duration-300 ease-in-out ${
                        error
                          ? "opacity-100 -translate-y-3"
                          : "opacity-0 -translate-y-2"
                      }`}
                    >
                      {error && <span className="errorMessage">{error}</span>}
                    </div>
                  )}
                    </div>
                  ))}
              </div>

              {/* Info Alert */}
              <CustomAlert
                type="warning"
                message="Please note: Refunds will be processed within 7 working days after approval. Nutricub will inspect the returned product to ensure quality before issuing a refund."
              />

              {/* Submit */}
              <div className="flex justify-end">
                <GeneralButton
                  type="Submit"
                  textColor="#fff"
                  className="text-20pt px-4"
                  onClick={() => {
                    setTimeout(() => scrollToFirstError(form.errors), 100);
                  }}
                  text={"Submit"}
                  disabled={status === "loading" || status === "success"}
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default OrderReturn;
